import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError, FIELD_OPTIONS, importEndpoints } from '@ooh/contracts';
import type { ImportJob, MediaType } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { TableSkeleton } from '../../layouts/skeletons';
import { MappingPhase } from './mapping-phase';
import type { ColumnMappingItem, MappingPhaseLabels } from './mapping-phase';
import { MappingConflictRedirect, useRedirectToCurrentPhase } from './mapping-conflict-redirect';
import { columnsRequest, confirmRequest } from '../api/queries';
import { useOptionalJobStatus } from '../hooks/use-optional-job-status';
import {
  buildFilteredFieldOptions,
  computeMissingRequiredFieldKeys,
  deriveColumnMappingProps,
  deriveMappingPhaseSourceExtras,
} from '../lib/mapping-phase-wizard-helpers';

export interface MappingPhaseSectionProps {
  importJobId: number;
  selectedSupplierId: string | null;
  onSupplierChange: (id: string | null) => void;
  onConfirmed: () => void;
  onPhaseChange: (phase: string, jobId?: number) => void;
  errorLabel: string;
  mappingLabels: MappingPhaseLabels;
  /** Per-file media type override; forwarded to importColumnsQueryOptions and confirmMapping. */
  mediaTypeOverride?: MediaType;
  /** Called when user picks/clears the override. Parent updates URL search param. */
  onMediaTypeOverrideChange?: (value: string | null) => void;
}

export function MappingPhaseSection({
  importJobId,
  selectedSupplierId,
  onSupplierChange,
  onConfirmed,
  onPhaseChange,
  errorLabel,
  mappingLabels,
  mediaTypeOverride,
  onMediaTypeOverrideChange,
}: MappingPhaseSectionProps) {
  // Explicit sheet pick — undefined means "use the API's default (first sheet)"
  // (column-mapping-ui spec, "Changing the sheet reloads the mapping columns").
  const [selectedSheetName, setSelectedSheetName] = React.useState<string | undefined>(undefined);
  const jobStatus = useOptionalJobStatus(importJobId);

  return (
    <QueryBoundary
      fallback={<TableSkeleton columns={5} />}
      renderError={(error) =>
        error instanceof ApiError && error.status === 409 ? (
          <MappingConflictRedirect importJobId={importJobId} onPhaseChange={onPhaseChange} />
        ) : undefined
      }
    >
      <LoadedMappingPhase
        importJobId={importJobId}
        jobStatus={jobStatus}
        selectedSupplierId={selectedSupplierId}
        selectedSheetName={selectedSheetName}
        onSheetChange={setSelectedSheetName}
        onSupplierChange={onSupplierChange}
        onConfirmed={onConfirmed}
        onPhaseChange={onPhaseChange}
        errorLabel={errorLabel}
        mappingLabels={mappingLabels}
        mediaTypeOverride={mediaTypeOverride}
        onMediaTypeOverrideChange={onMediaTypeOverrideChange}
      />
    </QueryBoundary>
  );
}

interface LoadedMappingPhaseProps extends MappingPhaseSectionProps {
  jobStatus: ImportJob | undefined;
  selectedSheetName: string | undefined;
  onSheetChange: (sheetName: string | undefined) => void;
}

function LoadedMappingPhase({
  importJobId,
  jobStatus,
  selectedSupplierId,
  selectedSheetName,
  onSheetChange,
  onSupplierChange,
  onConfirmed,
  onPhaseChange,
  errorLabel,
  mappingLabels,
  mediaTypeOverride,
  onMediaTypeOverrideChange,
}: LoadedMappingPhaseProps) {
  const api = useApiClient();
  const redirectToCurrentPhase = useRedirectToCurrentPhase(importJobId, onPhaseChange);
  const [mappingError, setMappingError] = React.useState<string | null>(null);

  // Deferred so a supplier / override / sheet change keeps the mapping table (and the operator's
  // in-progress manual column mappings and constants) on screen while the new columns load,
  // instead of swapping it for the skeleton and discarding that state.
  const deferredSupplierId = React.useDeferredValue(selectedSupplierId);
  const deferredMediaTypeOverride = React.useDeferredValue(mediaTypeOverride);
  const deferredSheetName = React.useDeferredValue(selectedSheetName);

  // Column data for a given job+supplier+override+sheet combination is immutable.
  const { data: columnsData } = useApiSuspenseQuery(
    importEndpoints.getColumns,
    columnsRequest(
      importJobId,
      deferredSupplierId ?? undefined,
      deferredMediaTypeOverride,
      deferredSheetName,
    ),
    { staleTime: Infinity },
  );

  const confirmMutation = useMutation({
    mutationFn: (args: { mappings: ColumnMappingItem[]; saveForSupplierId?: string }) =>
      api.call(
        importEndpoints.confirmMapping,
        confirmRequest(importJobId, args.mappings, args.saveForSupplierId, mediaTypeOverride),
      ),
    onSuccess: () => {
      setMappingError(null);
      onConfirmed();
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409) {
        void redirectToCurrentPhase();
        return;
      }
      setMappingError(errorLabel);
    },
  });

  const handleMappingDone = React.useCallback(
    (mappings: ColumnMappingItem[], saveForSupplierId?: string) => {
      setMappingError(null);
      confirmMutation.mutate({ mappings, saveForSupplierId });
    },
    [confirmMutation],
  );

  // Build filtered fieldOptions: when adapter columns are provided, filter to adapter's column set
  // and mark adapter-required columns as required=true.
  const effectiveFieldOptions = React.useMemo(
    () => buildFilteredFieldOptions(FIELD_OPTIONS, columnsData.adapterColumns),
    [columnsData.adapterColumns],
  );

  const derivedProps = React.useMemo(
    () => deriveColumnMappingProps(columnsData, deferredSupplierId),
    [columnsData, deferredSupplierId],
  );
  const sourceExtras = React.useMemo(
    () => deriveMappingPhaseSourceExtras(columnsData, jobStatus, deferredSheetName),
    [columnsData, jobStatus, deferredSheetName],
  );

  // Recompute missing required keys using the effective (potentially filtered) field options
  const missingRequiredFieldKeys = React.useMemo(
    () => computeMissingRequiredFieldKeys(derivedProps.columnMappings, effectiveFieldOptions),
    [derivedProps.columnMappings, effectiveFieldOptions],
  );

  return (
    <MappingPhase
      // Key changes when column data, supplier, or sheet changes — each of those genuinely
      // changes the detected column set, so React remounts MappingPhase with fresh state,
      // avoiding useEffect synchronisation anti-patterns. mediaTypeOverride is deliberately
      // EXCLUDED: it doesn't change the column set, only which fields are required/collapsed
      // (already reactive via the mediaTypeOverride PROP below), so keying on it would discard
      // in-progress transform/constant edits on every override change (item #2).
      key={`${importJobId}-${deferredSupplierId ?? 'none'}-${columnsData.detectedColumns.length}-${deferredSheetName ?? 'default'}`}
      columnMappings={derivedProps.columnMappings}
      fieldOptions={effectiveFieldOptions}
      suppliers={derivedProps.suppliers}
      supplierName={derivedProps.supplierName}
      hasSavedMapping={derivedProps.hasSavedMapping}
      labels={mappingLabels}
      missingRequiredFieldKeys={missingRequiredFieldKeys}
      onNext={handleMappingDone}
      onSupplierChange={onSupplierChange}
      errorMessage={mappingError}
      adapterColumns={columnsData.adapterColumns}
      mediaTypeOverride={mediaTypeOverride}
      onMediaTypeOverrideChange={onMediaTypeOverrideChange}
      columnSamples={derivedProps.columnSamples}
      columnDistinctValues={derivedProps.columnDistinctValues}
      transformSuggestions={derivedProps.transformSuggestions}
      manualFallbackMapping={sourceExtras.manualFallbackMapping}
      headerRowIndex={sourceExtras.headerRowIndex}
      sheets={sourceExtras.sheets}
      selectedSheet={sourceExtras.selectedSheet}
      onSheetChange={onSheetChange}
      fileName={sourceExtras.fileName}
      estimatedRows={sourceExtras.estimatedRows}
    />
  );
}
